import { useState, useEffect, useRef, useCallback } from 'react';
import { Room, RoomEvent, createLocalAudioTrack } from 'livekit-client';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useLiveCall } from '../../context/LiveCallContext';
import { Play, Mic, MicOff, PhoneOff, Loader2 } from 'lucide-react';
import { StreamingTranscript } from './StreamingTranscript';

export function CallWorkspace({ session, caseData, onCallEnd }) {
  const { token, user } = useAuth();
  const { startLiveTranscript, closeLiveTranscript } = useLiveCall();

  const [isConnecting, setIsConnecting] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [callStatus, setCallStatus] = useState('');
  
  const roomRef = useRef(null);
  const micTrackRef = useRef(null);
  const audioElementRef = useRef(null);
  const activeCallIdRef = useRef('');
  const isEndingCallRef = useRef(false);
  const isMicMutedRef = useRef(false);

  useEffect(() => {
    const audioElement = document.createElement('audio');
    audioElement.autoplay = true;
    document.body.appendChild(audioElement);
    audioElementRef.current = audioElement;

    return () => {
      const activeRoom = roomRef.current;
      if (activeRoom) {
        roomRef.current = null;
        void activeRoom.disconnect().catch(() => {});
      }
      if (micTrackRef.current) {
        try { micTrackRef.current.stop(); } catch (e) {}
        micTrackRef.current = null;
      }
      activeCallIdRef.current = '';
      isEndingCallRef.current = false;
      isMicMutedRef.current = false;
      audioElement.remove();
      audioElementRef.current = null;
      closeLiveTranscript();
    };
  }, [closeLiveTranscript]);

  const stopLocalMicTrack = useCallback(() => {
    if (!micTrackRef.current) return;
    try { micTrackRef.current.stop(); } catch (e) {}
    micTrackRef.current = null;
  }, []);

  const resetWebCallState = useCallback((nextStatus = '') => {
    activeCallIdRef.current = '';
    setIsCallActive(false);
    setIsConnecting(false);
    isEndingCallRef.current = false;
    setCallStatus(nextStatus);
    stopLocalMicTrack();
    closeLiveTranscript();
  }, [closeLiveTranscript, stopLocalMicTrack]);

  const handleToggleMute = useCallback(async () => {
    if (!micTrackRef.current) return;
    const nextMuted = !isMicMutedRef.current;
    try {
      if (nextMuted) {
        await micTrackRef.current.mute();
      } else {
        await micTrackRef.current.unmute();
      }
      setIsMicMuted(nextMuted);
      isMicMutedRef.current = nextMuted;
    } catch (err) {
      toast.error('Failed to update microphone state');
    }
  }, []);

  const handleHangup = useCallback(async () => {
    const callId = activeCallIdRef.current;
    const activeRoom = roomRef.current;
    if (!callId && !activeRoom) return;

    isEndingCallRef.current = true;
    setCallStatus('Hanging up...');
    
    // Close the SSE stream
    closeLiveTranscript();

    if (callId) {
      try {
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        // 1. Hangup LiveKit
        await axios.post(`${apiUrl}/api/v1/clinicalsim/sessions/${session.session_id}/hangup`, {}, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        
        // 2. Update ClinicalSim session status
        await axios.patch(`${apiUrl}/api/v1/clinicalsim/sessions/${session.session_id}`, {
          status: 'completed'
        }, { headers: { Authorization: `Bearer ${token}` } });
      } catch (err) {
        console.warn('Hangup or session update failed', err);
      } finally {
        if (onCallEnd) onCallEnd();
      }
    }

    roomRef.current = null;
    stopLocalMicTrack();

    if (activeRoom) {
      try { await activeRoom.disconnect(); } catch (e) {}
    }

    resetWebCallState('Call ended');
  }, [closeLiveTranscript, resetWebCallState, stopLocalMicTrack, token, session.session_id, onCallEnd]);

  const handleStartCall = async () => {
    if (isConnecting || isCallActive) return;

    try {
      setIsConnecting(true);
      setCallStatus('Connecting to patient...');

      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.post(`${apiUrl}/api/v1/clinicalsim/sessions/${session.session_id}/call-token`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { token: roomToken, ws_url, room_name, call_id, participant_identity } = res.data;

      if (!roomToken || !ws_url || !call_id) {
        throw new Error('Missing LiveKit connection details');
      }

      // Update session with call_id so backend can track it
      await axios.patch(`${apiUrl}/api/v1/clinicalsim/sessions/${session.session_id}`, {
        call_id: call_id,
        status: 'in_progress'
      }, { headers: { Authorization: `Bearer ${token}` } });

      const newRoom = new Room();
      newRoom.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === 'audio' && audioElementRef.current) {
          track.attach(audioElementRef.current);
          audioElementRef.current.play().catch(() => {});
        }
      });
      newRoom.on(RoomEvent.Disconnected, async () => {
        roomRef.current = null;
        resetWebCallState(isEndingCallRef.current ? 'Call ended' : 'Call disconnected');
        
        // If the agent initiated the hangup or connection dropped, transition to prescription
        if (!isEndingCallRef.current && activeCallIdRef.current) {
          try {
            await axios.patch(`${apiUrl}/api/v1/clinicalsim/sessions/${session.session_id}`, {
              status: 'completed'
            }, { headers: { Authorization: `Bearer ${token}` } });
            if (onCallEnd) onCallEnd();
          } catch (err) {
            console.warn('Agent hangup session update failed', err);
          }
        }
      });

      await newRoom.connect(ws_url, roomToken);
      const micTrack = await createLocalAudioTrack();
      await newRoom.localParticipant.publishTrack(micTrack);

      isEndingCallRef.current = false;
      roomRef.current = newRoom;
      micTrackRef.current = micTrack;
      activeCallIdRef.current = call_id;
      setIsCallActive(true);
      setIsConnecting(false);

      startLiveTranscript({
        call_id: call_id,
        room_name: room_name,
        channel: 'web',
        participant_identity: participant_identity,
        agent_name: caseData.title,
        customer_name: caseData.patient.name,
        contact_name: caseData.patient.name,
        callStatus: 'Connected',
        isMicMuted: false,
        onToggleMute: handleToggleMute,
        onHangup: handleHangup,
        endpointPath: `/api/v1/clinicalsim/sessions/${session.session_id}/live-transcript/stream`,
        fallbackPath: `/api/v1/clinicalsim/sessions/${session.session_id}/live-transcript`,
      });

      setCallStatus('Connected');
      toast.success('Patient connected');

    } catch (err) {
      console.error('Call start error:', err);
      resetWebCallState('Failed to connect');
      toast.error('Failed to connect to patient');
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-8">
      {!isCallActive && !isConnecting ? (
        <div className="text-center max-w-sm">
          <div className="w-24 h-24 bg-violet-50 rounded-full flex items-center justify-center mx-auto mb-6 border-8 border-violet-50/50">
            <Play className="w-8 h-8 text-violet-500 ml-2" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Ready to Start</h2>
          <p className="text-slate-500 mb-8">
            Click below to initiate the voice session with {caseData.patient.name}. Make sure your microphone is enabled.
          </p>
          <button
            onClick={handleStartCall}
            className="w-full py-4 bg-violet-500 hover:bg-violet-600 text-white rounded-2xl font-bold text-lg shadow-lg shadow-violet-500/25 transition-all transform hover:-translate-y-0.5 active:translate-y-0"
          >
            Start Web Call
          </button>
        </div>
      ) : (
        <div className="w-full h-full flex flex-col max-w-4xl">
          {/* Active Call UI */}
          <div className="flex-1 bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden flex flex-col mb-6">
            
            {/* Call Header */}
            <div className="bg-slate-900 text-white p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center font-bold text-lg border border-slate-700">
                    {caseData.patient.initials || 'PT'}
                  </div>
                  {isCallActive && (
                    <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-violet-500 border-2 border-slate-900 rounded-full animate-pulse" />
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-lg">{caseData.patient.name}</h3>
                  <p className="text-sm text-slate-400 font-medium">{callStatus}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-mono font-bold tracking-tight">
                  {/* Real implementation would have a timer here */}
                  00:00
                </div>
                <div className="text-xs font-bold text-violet-400 uppercase tracking-widest mt-1">
                  Secure Audio Session
                </div>
              </div>
            </div>

            {/* Transcript Area */}
            <div className="flex-1 bg-slate-50 p-6 relative">
              {isConnecting ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <Loader2 className="w-8 h-8 text-violet-500 animate-spin mb-4" />
                  <p className="text-slate-500 font-medium">Establishing secure connection...</p>
                </div>
              ) : (
                <StreamingTranscript />
              )}
            </div>
          </div>

          {/* Call Controls */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={handleToggleMute}
              disabled={!isCallActive}
              className={`p-5 rounded-full shadow-lg transition-all ${
                isMicMuted 
                  ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                  : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
              }`}
            >
              {isMicMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
            </button>
            <button
              onClick={handleHangup}
              disabled={!isCallActive && !isConnecting}
              className="px-8 py-5 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg shadow-red-500/25 flex items-center gap-3 font-bold transition-transform hover:scale-105"
            >
              <PhoneOff className="w-5 h-5" />
              End Session
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
